import type {Layer, LayerContext, Accessor} from '@deck.gl/core';
import {LayerExtension} from '@deck.gl/core';
import { getVariableShaderTypeInfo, type VariableShaderType } from '@luma.gl/core';

type PaletteMap = Record<string, number[][]>; // e.g. { fillColor: [[r,g,b,a], ...], radius: [value] }


export type MathExtensionProps<DataT = any> = {
    getCategoryIndex?: Accessor<DataT, number>;
};

type GlslType = 'float' | 'vec2' | 'vec3' | 'vec4' | 'int' | 'ivec2' | 'ivec3' | 'ivec4' | 'mat2' | 'mat3' | 'mat4';
type UniformValue = number | number[] | number[][];
type Uniform = {
    type: GlslType;
    utype: VariableShaderType; // e.g. 'f32', 'vec2<f32>', etc.
    value: UniformValue; 
    packed?: number | Float32Array; // packed version for arrays
    length?: number; // for arrays
}

type Attribute = {
    type: GlslType;
    value?: UniformValue;
}

export type MathExtensionOptions = {
    name?: string;
    attrs?: Record<string, Attribute>;
    uniforms: Record<string, Uniform>;
    inject?: Record<string, string>;
    vs?: string;
    fs?: string;
    noUniformBlock?: boolean;
};

type MathExtensionDefaultProps = Record<string, any>;

const packFloat32Array = (arr: number[][]) => {
  const flat = new Float32Array(arr.length * (arr[0]?.length === undefined ? 1 : arr[0].length || 0));
  let i = 0;
  for (const rgba of arr) {
    for (const v of (Array.isArray(rgba) ? rgba : [rgba])) {
      flat[i++] = v;
    }
  }
  return flat;
}

// const packUniformValue = (uniform: Uniform) => {
//     if (Array.isArray(uniform.value)) {
//         return packFloat32Array(uniform.value);
//     } else {
//         return uniform.value;
//     }
// }

// const processOpts = (opts: MathExtensionOptions) => {
//   for (const [name, uniform] of Object.entries(opts.uniforms)) {
//     if (Array.isArray(uniform.value)) {
//         uniform.packed = packFloat32Array(uniform.value);
//         uniform.length = uniform.value.length;
//     } else {
//         uniform.packed = uniform.value;
//     }
//   }
//   return opts;
// }

const declaration = (inout: string, type: GlslType, name: string, length?: number) => {
    return (
      (type === 'int' ? 'flat ' : '') +
      `${inout} ${type} ${name}${length && length > 1 ? `[${length}]` : ''};`
    )
}

export class MathExtension extends LayerExtension<Required<MathExtensionOptions>> {
  static extensionName = 'MathExtension';
  defaultProps: MathExtensionDefaultProps = {};

  constructor(opts: MathExtensionOptions) {
    opts.name = opts.name || 'dictEncoding';
    // opts = processOpts(opts);
    // console.log(opts)

    super(opts);

    const defaultProps = Object.entries(this.opts.attrs).reduce((acc, [attr]) => {
      acc[`get${capitalizeFirstLetter(attr)}`] = {
        type: 'accessor' as const,
        value: null,
      };
      return acc;
    }, {} as MathExtensionDefaultProps);
    
    this.defaultProps = { ...this.defaultProps, ...defaultProps };
  }

  getShaders(this: Layer<MathExtensionProps>, extension: this) {
    const name = extension.opts.name || 'dictEncoding';
    const attrs = extension.opts.attrs || {};
    const uniforms: MathExtensionOptions['uniforms'] = extension.opts.uniforms || {};
    const injectFn = extension.opts.inject || {};
    const inject = typeof injectFn === 'function' ? injectFn(extension) : injectFn;

    let uniformBlock = '';
    if (extension.opts.noUniformBlock) {
      uniformBlock = Object.entries(uniforms).map(([name, val]) => (
        declaration('uniform', val.type, name, val.length || val.value?.length)
      )).join('\n').trim();
    } else {
      const uniformDefs = Object.entries(uniforms).map(([name, val]) => (
        declaration('', val.type, name, val.length || val.value?.length)
      )).join('\n').trim();
      uniformBlock = uniformDefs ? `
      uniform ${name}Uniforms {
      ${uniformDefs}
      } ${name};`.trim() : '';
    }

    const vertexInputs = Object.entries(attrs).map(([attr, {type, size}]) => 
      declaration('in', type, `instance${capitalizeFirstLetter(attr)}`, size)
    ).join('\n').trim();
    const vertexOutputs = Object.entries(attrs).map(([attr, {type, size}]) =>
      declaration('out', type, `vInstance${capitalizeFirstLetter(attr)}`, size)
    ).join('\n').trim();
    const vertexMainEnd = Object.entries(attrs).map(([attr]) =>
      `vInstance${capitalizeFirstLetter(attr)} = instance${capitalizeFirstLetter(attr)};`
    ).join('\n').trim();
    const fragmentInputs = Object.entries(attrs).map(([attr, {type, size}]) =>
      declaration('in', type, `vInstance${capitalizeFirstLetter(attr)}`, size)
    ).join('\n').trim();

    const module = {
      name,

      inject: {
        ...inject,
        'vs:#decl': `
        ${vertexInputs}
        ${vertexOutputs}
        ${inject['vs:#decl'] || ''}
        `.trim(),
        'vs:#main-end': `
        ${vertexMainEnd}
        ${inject['vs:#main-end'] || ''}
        `.trim(),
        'fs:#decl': `
        ${fragmentInputs}
        ${inject['fs:#decl'] || ''}
        `.trim(),
        // 'fs:DECKGL_FILTER_COLOR': `
        // ${inject['fs:DECKGL_FILTER_COLOR'] || ''}
        // `.trim(),
      },
      // Inject uniform block into both shaders so the struct is defined.
      vs: `
      ${uniformBlock}
      ${extension.opts.vs || ''}
      `.trim(),
      fs: `
      ${uniformBlock}
      ${extension.opts.fs || ''}
      `.trim(),
      // Map shader module props -> actual GLSL uniforms
      getUniforms: (opts: MathExtensionOptions['uniforms']) => {
        // console.log('getUniforms', opts);
        const uniforms = Object.entries(opts || {}).reduce((acc, [name, val]) => {
          // acc[name] = val;
          if (Array.isArray(val.value)) acc[name] = packFloat32Array(val.value);
          // else if (val.type === 'sampler2D') {
          //   acc[name] = val.value; // textures are passed as-is
          //   acc[`${name}Size`] = val.value; // add sampler uniform too
          // }
          else acc[name] = val.value;
          return acc;
        }, {} as Record<string, number | number[] | Float32Array>);
        return uniforms;
      },
      uniformTypes: Object.fromEntries(Object.entries(uniforms || {}).map(
        ([name, val]) => [name, val.utype]))
    }
    // console.log('MathExtension module:', module);
    // console.log('MathExtension uniforms:', module.getUniforms(extension.opts));
    return { modules: [module]};
  }

  initializeState(this: Layer<MathExtensionProps>, context: LayerContext, extension: this) {
    super.initializeState(context, extension);
    const attributeManager = this.getAttributeManager();
    if (!attributeManager) {
      return;
    }
    attributeManager.add(Object.entries(extension.opts.attrs || {}).reduce((acc, [attr, {type, ...attrOpts}]: [string, Attribute]) => {
      acc[`instance${capitalizeFirstLetter(attr) }`] = {
        size: 1,
        stepMode: 'dynamic',
        accessor: `get${capitalizeFirstLetter(attr) }`,
        ...attrOpts,
      };
      return acc;
    }, {} as Record<string, any>));
  }

  // /** Returns true if two extensions are equivalent */
  // equals(extension: LayerExtension<OptionsT>): boolean {
  //   console.log(
  //     'MathExtension equals: comparing instances', 
  //     this === extension, 
  //     this.constructor === extension.constructor, 
  //     super.equals.call(this, extension), this.opts, extension.opts);
  //   // Compare extensions shallowly
  //   return super.equals.call(this, extension);
  // }


  draw(this: Layer<MathExtensionProps>, params: any, extension: this) {
    // Pass palette data into the shader module so deck.gl/luma can bind uniforms.
    this.setShaderModuleProps({
        [`${extension.opts.name}`]: extension.opts.uniforms,
    });
    // this.setNeedsRedraw();
    // this.setNeedsUpdate();
    // super.draw(params, extension);
    // console.log('MathExtension draw', extension.opts.uniforms.currentDepth.value);
  }
}

function capitalizeFirstLetter(val: string) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}



export const CreateMathExtensionSubclass = (options: MathExtensionOptions) => {
  // Create default props from attributes
  const defaultProps = Object.entries(options.attrs || {}).reduce((acc, [attr, { value }]) => {
    acc[`get${capitalizeFirstLetter(attr)}`] = {
      type: 'accessor' as const,
      value: value ?? null,
    };
    return acc;
  }, {} as MathExtensionDefaultProps);

  const SubMathExtension = class extends MathExtension {
    static extensionName = options.name || 'SubMathExtension';
    defaultProps = defaultProps;
    constructor(name: string, uniforms: Record<string, UniformValue>) {
      options.name = name || SubMathExtension.extensionName;
      for (const name of Object.keys(options.uniforms)) {
        if (uniforms[name] !== undefined) {
          options.uniforms[name].value = uniforms[name];
        }
      }
      // options = { ...options, uniforms: {...options.uniforms} };
      // for (const name of Object.keys(options.uniforms)) {
      //   if (uniforms[name] !== undefined) {
      //     options.uniforms[name].value = uniforms[name];
      //     // options.uniforms[name] = {
      //     //   ...options.uniforms[name],
      //     //   value: uniforms[name]
      //     // }
      //   }
      // }
      super(options);
    }

  }
  return SubMathExtension;
}